/**
 * Service d'amélioration de l'accessibilité pour les sections informatives
 * Implémente les exigences: 1.1, 1.2, 3.1, 4.2
 * 
 * Fonctionnalités:
 * - Navigation clavier pour les carousels et grilles
 * - Annonces ARIA pour les changements de contenu
 * - Support des lecteurs d'écran pour les éléments interactifs
 * - Gestion des préférences d'accessibilité
 */

import { 
  KeyboardNavigationManager, 
  AccessibilityUtils, 
  ariaAnnouncer 
} from '../utils/accessibility-utils.js';

/**
 * Interface pour les options d'amélioration d'accessibilité
 */
interface AccessibilityEnhancementOptions {
  enableKeyboardNavigation?: boolean;
  enableAriaAnnouncements?: boolean;
  enableFocusManagement?: boolean;
  enableHighContrast?: boolean;
  enableReducedMotion?: boolean;
}

/**
 * Service d'amélioration de l'accessibilité
 */
export class AccessibilityEnhancementService {
  private static instance: AccessibilityEnhancementService;
  private keyboardManagers: Map<string, KeyboardNavigationManager> = new Map();
  private options: AccessibilityEnhancementOptions;

  private constructor(options: AccessibilityEnhancementOptions = {}) {
    this.options = {
      enableKeyboardNavigation: true,
      enableAriaAnnouncements: true,
      enableFocusManagement: true,
      enableHighContrast: true,
      enableReducedMotion: true,
      ...options
    };

    this.initialize();
  }

  static getInstance(options?: AccessibilityEnhancementOptions): AccessibilityEnhancementService {
    if (!AccessibilityEnhancementService.instance) {
      AccessibilityEnhancementService.instance = new AccessibilityEnhancementService(options);
    }
    return AccessibilityEnhancementService.instance;
  }

  /**
   * Initialise le service d'accessibilité
   */
  private initialize(): void {
    if (this.options.enableHighContrast || this.options.enableReducedMotion) {
      AccessibilityUtils.applyAccessibilityPreferences();
    }

    // Écouter les changements de préférences système
    this.setupPreferenceListeners();
  }

  /**
   * Configure les listeners pour les préférences d'accessibilité
   */
  private setupPreferenceListeners(): void {
    // Écouter les changements de mouvement réduit
    if (this.options.enableReducedMotion) {
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
        this.handleReducedMotionChange(e.matches);
      });
    }

    // Écouter les changements de contraste élevé
    if (this.options.enableHighContrast) {
      window.matchMedia('(prefers-contrast: high)').addEventListener('change', (e) => {
        this.handleHighContrastChange(e.matches);
      });
    }
  }

  /**
   * Gère les changements de préférence de mouvement réduit
   */
  private handleReducedMotionChange(prefersReduced: boolean): void {
    document.body.classList.toggle('reduce-motion', prefersReduced);
    
    if (this.options.enableAriaAnnouncements) {
      ariaAnnouncer.announce({
        message: prefersReduced ? 
          'Animations réduites activées' : 
          'Animations normales restaurées',
        priority: 'polite'
      });
    }
  }

  /**
   * Gère les changements de préférence de contraste élevé
   */
  private handleHighContrastChange(prefersHigh: boolean): void {
    document.body.classList.toggle('high-contrast', prefersHigh);
    
    if (this.options.enableAriaAnnouncements) {
      ariaAnnouncer.announce({
        message: prefersHigh ? 
          'Mode contraste élevé activé' : 
          'Mode contraste normal restauré',
        priority: 'polite'
      });
    }
  }

  /**
   * Améliore l'accessibilité d'une section de destinations populaires
   */
  enhancePopularDestinationsSection(container: HTMLElement): void {
    // Ajouter un titre de section accessible
    this.ensureSectionHeading(container, 'Destinations populaires');

    // Améliorer la navigation clavier dans la grille
    if (this.options.enableKeyboardNavigation) {
      const keyboardManager = new KeyboardNavigationManager(container, {
        arrowNavigation: true,
        skipLinks: false
      });
      this.keyboardManagers.set('popular-destinations', keyboardManager);
    }

    // Améliorer les cartes de destination
    const destinationCards = container.querySelectorAll('.destination-card');
    destinationCards.forEach((card, index) => {
      this.enhanceDestinationCard(card as HTMLElement, index);
    });

    // Ajouter des annonces pour les changements de contenu
    if (this.options.enableAriaAnnouncements) {
      this.setupContentChangeAnnouncements(container, 'destinations populaires');
    }
  }

  /**
   * Améliore l'accessibilité d'une carte de destination
   */
  private enhanceDestinationCard(card: HTMLElement, _index: number): void {
    // S'assurer que la carte est focusable
    if (!card.hasAttribute('tabindex')) {
      card.setAttribute('tabindex', '0');
    }

    // Ajouter un rôle approprié
    card.setAttribute('role', 'article');

    // Créer un label accessible
    const title = card.querySelector('.destination-card__title')?.textContent;
    const price = card.querySelector('.destination-card__price')?.textContent;
    
    if (title) {
      const ariaLabel = price ? 
        `${title}, à partir de ${price}` : 
        title;
      card.setAttribute('aria-label', ariaLabel);
    }

    // Améliorer l'accessibilité du bouton
    const button = card.querySelector('button, a');
    if (button) {
      AccessibilityUtils.enhanceButtonAccessibility(button as HTMLElement);
    }

    // Ajouter la gestion du clic sur la carte entière
    card.addEventListener('click', (e) => {
      if (e.target === card || card.contains(e.target as Node)) {
        const link = card.querySelector('a, button') as HTMLElement;
        if (link && e.target !== link) {
          link.click();
        }
      }
    });

    // Gestion du clavier pour la carte
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const link = card.querySelector('a, button') as HTMLElement;
        if (link) {
          link.click();
        }
      }
    });
  }

  /**
   * Améliore l'accessibilité d'une section météo
   */
  enhanceWeatherSection(container: HTMLElement): void {
    this.ensureSectionHeading(container, 'Informations météo');

    // Améliorer les cartes météo
    const weatherCards = container.querySelectorAll('.weather-card');
    weatherCards.forEach((card, index) => {
      this.enhanceWeatherCard(card as HTMLElement, index);
    });

    // Configuration de la navigation clavier
    if (this.options.enableKeyboardNavigation) {
      const keyboardManager = new KeyboardNavigationManager(container, {
        arrowNavigation: true
      });
      this.keyboardManagers.set('weather-section', keyboardManager);
    }
  }

  /**
   * Améliore l'accessibilité d'une carte météo
   */
  private enhanceWeatherCard(card: HTMLElement, _index: number): void {
    card.setAttribute('role', 'article');
    card.setAttribute('tabindex', '0');

    // Créer un label accessible pour la météo
    const city = card.querySelector('.weather-card__city')?.textContent;
    const temp = card.querySelector('.weather-card__temp')?.textContent;
    const condition = card.querySelector('.weather-card__condition')?.textContent;

    if (city && temp) {
      const ariaLabel = condition ? 
        `Météo à ${city}: ${temp}, ${condition}` : 
        `Météo à ${city}: ${temp}`;
      card.setAttribute('aria-label', ariaLabel);
    }

    // Masquer les icônes décoratives des lecteurs d'écran
    const icons = card.querySelectorAll('.weather-icon, .weather-card__icon');
    icons.forEach(icon => {
      icon.setAttribute('aria-hidden', 'true');
    });
  }

  /**
   * Améliore l'accessibilité d'une section de conseils de voyage
   */
  enhanceTravelTipsSection(container: HTMLElement): void {
    this.ensureSectionHeading(container, 'Conseils de voyage');

    // Améliorer les articles de conseils
    const tipArticles = container.querySelectorAll('.travel-tip, .tip-card');
    tipArticles.forEach((article, index) => {
      this.enhanceTravelTipArticle(article as HTMLElement, index);
    });

    // Navigation clavier pour les conseils
    if (this.options.enableKeyboardNavigation) {
      const keyboardManager = new KeyboardNavigationManager(container, {
        arrowNavigation: true
      });
      this.keyboardManagers.set('travel-tips', keyboardManager);
    }
  }

  /**
   * Améliore l'accessibilité d'un article de conseil
   */
  private enhanceTravelTipArticle(article: HTMLElement, _index: number): void {
    article.setAttribute('role', 'article');
    article.setAttribute('tabindex', '0');

    // Améliorer le titre de l'article
    const title = article.querySelector('h3, h4, .tip-title');
    if (title && !title.id) {
      title.id = AccessibilityUtils.generateId('tip-title');
      article.setAttribute('aria-labelledby', title.id);
    }

    // Améliorer la description
    const description = article.querySelector('.tip-description, .tip-content p');
    if (description && !description.id) {
      description.id = AccessibilityUtils.generateId('tip-desc');
      article.setAttribute('aria-describedby', description.id);
    }

    // Améliorer les liens "Lire plus"
    const readMoreLink = article.querySelector('.read-more, .tip-link');
    if (readMoreLink) {
      AccessibilityUtils.enhanceButtonAccessibility(readMoreLink as HTMLElement);
      
      // Améliorer le texte du lien
      const linkText = readMoreLink.textContent?.trim();
      if (linkText === 'Lire plus' || linkText === 'En savoir plus') {
        const tipTitle = title?.textContent?.trim();
        if (tipTitle) {
          readMoreLink.setAttribute('aria-label', `Lire plus sur: ${tipTitle}`);
        }
      }
    }
  }

  /**
   * Améliore l'accessibilité d'une section d'offres spéciales
   */
  enhanceSpecialOffersSection(container: HTMLElement): void {
    this.ensureSectionHeading(container, 'Offres spéciales');

    // Améliorer les cartes d'offres
    const offerCards = container.querySelectorAll('.offer-card, .special-offer');
    offerCards.forEach((card, index) => {
      this.enhanceOfferCard(card as HTMLElement, index);
    });

    // Navigation clavier pour les offres
    if (this.options.enableKeyboardNavigation) {
      const keyboardManager = new KeyboardNavigationManager(container, {
        arrowNavigation: true
      });
      this.keyboardManagers.set('special-offers', keyboardManager);
    }
  }

  /**
   * Améliore l'accessibilité d'une carte d'offre
   */
  private enhanceOfferCard(card: HTMLElement, _index: number): void {
    card.setAttribute('role', 'article');
    card.setAttribute('tabindex', '0');

    // Créer un label accessible pour l'offre
    const title = card.querySelector('.offer-title, .offer-card__title')?.textContent;
    const price = card.querySelector('.offer-price, .offer-card__price')?.textContent;
    const discount = card.querySelector('.offer-discount, .discount-badge')?.textContent;

    if (title) {
      let ariaLabel = title;
      if (discount) ariaLabel += `, ${discount}`;
      if (price) ariaLabel += `, ${price}`;
      card.setAttribute('aria-label', ariaLabel);
    }

    // Améliorer les badges de réduction
    const discountBadge = card.querySelector('.discount-badge, .offer-badge');
    if (discountBadge) {
      discountBadge.setAttribute('role', 'status');
      discountBadge.setAttribute('aria-label', `Réduction: ${discountBadge.textContent}`);
    }

    // Améliorer les dates de validité
    const validityDate = card.querySelector('.offer-validity, .valid-until');
    if (validityDate) {
      validityDate.setAttribute('aria-label', `Valide jusqu'au ${validityDate.textContent}`);
    }

    // Améliorer le bouton d'action
    const actionButton = card.querySelector('button, a');
    if (actionButton) {
      AccessibilityUtils.enhanceButtonAccessibility(actionButton as HTMLElement);
      
      if (title && !actionButton.getAttribute('aria-label')) {
        actionButton.setAttribute('aria-label', `Réserver l'offre: ${title}`);
      }
    }
  }

  /**
   * S'assure qu'une section a un titre accessible
   */
  private ensureSectionHeading(container: HTMLElement, defaultTitle: string): void {
    let heading = container.querySelector('h1, h2, h3, h4, h5, h6');
    
    if (!heading) {
      // Chercher un titre dans les classes communes
      heading = container.querySelector('.section-title, .title, .heading');
    }

    if (!heading) {
      // Créer un titre accessible caché
      const hiddenHeading = document.createElement('h2');
      hiddenHeading.textContent = defaultTitle;
      hiddenHeading.className = 'sr-only';
      container.insertBefore(hiddenHeading, container.firstChild);
      heading = hiddenHeading;
    }

    // S'assurer que le titre a un ID pour les références
    if (!heading.id) {
      heading.id = AccessibilityUtils.generateId('section-heading');
    }

    // Associer le titre à la section
    container.setAttribute('aria-labelledby', heading.id);
  }

  /**
   * Configure les annonces pour les changements de contenu
   */
  private setupContentChangeAnnouncements(container: HTMLElement, sectionName: string): void {
    // Observer les changements dans le conteneur
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Annoncer les nouveaux contenus
          const addedElements = Array.from(mutation.addedNodes)
            .filter(node => node.nodeType === Node.ELEMENT_NODE);
          
          if (addedElements.length > 0) {
            ariaAnnouncer.announce({
              message: `Nouveau contenu ajouté dans la section ${sectionName}`,
              priority: 'polite',
              delay: 500
            });
          }
        }
      });
    });

    observer.observe(container, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Améliore l'accessibilité d'un carousel
   */
  enhanceCarousel(container: HTMLElement, options: { autoplay?: boolean } = {}): void {
    const carousel = container.querySelector('.carousel, .slider');
    if (!carousel) return;

    // Ajouter les attributs ARIA pour le carousel
    carousel.setAttribute('role', 'region');
    carousel.setAttribute('aria-label', 'Carousel d\'images');

    // Améliorer les contrôles de navigation
    const prevBtn = carousel.querySelector('.carousel-prev, .prev-btn');
    const nextBtn = carousel.querySelector('.carousel-next, .next-btn');

    if (prevBtn) {
      AccessibilityUtils.enhanceButtonAccessibility(prevBtn as HTMLElement);
      prevBtn.setAttribute('aria-label', 'Image précédente');
    }

    if (nextBtn) {
      AccessibilityUtils.enhanceButtonAccessibility(nextBtn as HTMLElement);
      nextBtn.setAttribute('aria-label', 'Image suivante');
    }

    // Améliorer les indicateurs de pagination
    const indicators = carousel.querySelectorAll('.carousel-indicator, .dot');
    indicators.forEach((indicator, index) => {
      AccessibilityUtils.enhanceButtonAccessibility(indicator as HTMLElement);
      indicator.setAttribute('aria-label', `Aller à l'image ${index + 1}`);
    });

    // Gérer l'autoplay et l'accessibilité
    if (options.autoplay) {
      this.handleCarouselAutoplay(carousel as HTMLElement);
    }

    // Navigation clavier pour le carousel
    if (this.options.enableKeyboardNavigation) {
      this.setupCarouselKeyboardNavigation(carousel as HTMLElement);
    }
  }

  /**
   * Gère l'autoplay du carousel avec accessibilité
   */
  private handleCarouselAutoplay(carousel: HTMLElement): void {
    // Ajouter un bouton de pause/lecture
    const controlsContainer = carousel.querySelector('.carousel-controls');
    if (controlsContainer) {
      const playPauseBtn = document.createElement('button');
      playPauseBtn.className = 'carousel-play-pause';
      playPauseBtn.setAttribute('aria-label', 'Mettre en pause le carousel automatique');
      playPauseBtn.innerHTML = '<span aria-hidden="true">⏸️</span>';
      controlsContainer.appendChild(playPauseBtn);

      // Gérer la pause sur focus ou survol
      carousel.addEventListener('mouseenter', () => {
        carousel.setAttribute('data-paused', 'true');
      });

      carousel.addEventListener('mouseleave', () => {
        carousel.removeAttribute('data-paused');
      });

      carousel.addEventListener('focusin', () => {
        carousel.setAttribute('data-paused', 'true');
      });

      carousel.addEventListener('focusout', () => {
        carousel.removeAttribute('data-paused');
      });
    }
  }

  /**
   * Configure la navigation clavier pour un carousel
   */
  private setupCarouselKeyboardNavigation(carousel: HTMLElement): void {
    carousel.addEventListener('keydown', (e) => {
      const { key } = e;
      
      switch (key) {
        case 'ArrowLeft':
          e.preventDefault();
          const prevBtn = carousel.querySelector('.carousel-prev, .prev-btn') as HTMLElement;
          if (prevBtn) prevBtn.click();
          break;

        case 'ArrowRight':
          e.preventDefault();
          const nextBtn = carousel.querySelector('.carousel-next, .next-btn') as HTMLElement;
          if (nextBtn) nextBtn.click();
          break;

        case 'Home':
          e.preventDefault();
          const firstIndicator = carousel.querySelector('.carousel-indicator:first-child') as HTMLElement;
          if (firstIndicator) firstIndicator.click();
          break;

        case 'End':
          e.preventDefault();
          const lastIndicator = carousel.querySelector('.carousel-indicator:last-child') as HTMLElement;
          if (lastIndicator) lastIndicator.click();
          break;
      }
    });
  }

  /**
   * Nettoie les ressources du service
   */
  destroy(): void {
    // Nettoyer tous les gestionnaires de navigation clavier
    this.keyboardManagers.forEach(manager => {
      manager.destroy();
    });
    this.keyboardManagers.clear();
  }

  /**
   * Met à jour les options du service
   */
  updateOptions(newOptions: Partial<AccessibilityEnhancementOptions>): void {
    this.options = { ...this.options, ...newOptions };
  }
}

/**
 * Instance globale du service d'amélioration d'accessibilité
 */
export const accessibilityEnhancer = AccessibilityEnhancementService.getInstance();